import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PawPrint, Milk, TrendingUp, Repeat, Baby, HeartPulse, ArrowUpRight, IndianRupee, Receipt } from 'lucide-react';
import { animalsApi } from '../../services/resources';
import { getApiErrorMessage } from '../../services/api';
import { useReferenceData } from '../../features/common/referenceData';
import { ILMSPageHeader } from '../../components/ILMSPageHeader';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSKPI } from '../../components/ILMSKPI';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { ILMSEmptyState } from '../../components/ILMSEmptyState';
import { ILMSCardSkeleton } from '../../components/ILMSLoadingState';
import { ILMSAnimalPicker } from '../../components/ILMSAnimalPicker';
import { ILMSTable, type ILMSTableColumn } from '../../components/ILMSTable';
import { cn } from '../../utils/cn';
import { formatCurrency, formatDate, formatNumber } from '../../utils/format';
import { SmartChart } from '../../features/charts/SmartChart';
import type { AnimalLifecycle, LifecycleExpenditureItem, LifecycleFamilyMember } from '../../types';

function ageLabel(days: number | null): string {
  if (days == null) return '—';
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  if (years > 0) return `${years}y ${months}m`;
  if (months > 0) return `${months}m`;
  return `${days}d`;
}

type NodeInfo = LifecycleFamilyMember | { id: string; animal_id: string; animal_name?: string | null; gender: string; primary_tag?: string | null; health_status: string; pending_tag?: false };

function nodeTooltip(member: NodeInfo): string {
  const lines = [
    member.animal_name ? `Name: ${member.animal_name}` : null,
    member.animal_id ? `Animal ID: ${member.animal_id}` : null,
    `Tag: ${member.primary_tag ?? 'Not yet tagged'}`,
    member.gender ? `Gender: ${member.gender}` : null,
    'health_status' in member && member.health_status ? `Health: ${member.health_status}` : null,
    'pending_tag' in member && member.pending_tag ? 'Logged in Calf Records — not yet registered as its own Animal.' : null,
  ];
  return lines.filter(Boolean).join('\n');
}

function FamilyNode({ member, label, isCenter, onClick }: { member: NodeInfo; label?: string; isCenter?: boolean; onClick?: () => void }) {
  const pending = 'pending_tag' in member && member.pending_tag;
  const clickable = !!onClick && !pending;
  return (
    <button
      type="button"
      title={nodeTooltip(member)}
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={cn(
        'flex flex-col items-center gap-1 rounded-lg border px-4 py-3 min-w-[150px] text-center',
        isCenter ? 'border-brand-green-600 bg-brand-green-50 shadow-card' : pending ? 'border-dashed border-warning-300 bg-warning-50' : 'border-ink-300/20 bg-white',
        clickable ? 'hover:border-brand-green-400 cursor-pointer transition-colors' : 'cursor-default'
      )}
    >
      {label && <span className="text-helper-text text-ink-400 uppercase tracking-wide">{label}</span>}
      <span className="text-card-title text-ink-900">{member.animal_id ?? member.animal_name ?? 'Unnamed'}</span>
      {member.animal_id && member.animal_name && <span className="text-secondary text-ink-500 truncate max-w-[130px]">{member.animal_name}</span>}
      <div className="flex items-center gap-1.5 mt-1 text-helper-text text-ink-400">
        <span>{member.gender === 'Male' ? '♂' : member.gender === 'Female' ? '♀' : '—'}</span>
        <span>{member.primary_tag ?? 'No tag'}</span>
      </div>
      {pending && <span className="text-helper-text text-warning-700 mt-0.5">Tag pending</span>}
    </button>
  );
}

function EmptyNode({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-ink-300/30 px-4 py-3 min-w-[150px] text-center text-ink-300">
      <span className="text-helper-text uppercase tracking-wide">{label}</span>
      <span className="text-secondary">Not recorded</span>
    </div>
  );
}

function FamilyTree({ lifecycle, onSelect }: { lifecycle: AnimalLifecycle; onSelect: (id: string) => void }) {
  const { animal, dam, sire, offspring } = lifecycle;
  return (
    <div className="flex flex-col items-center gap-4 py-2 overflow-x-auto">
      <div className="flex gap-6">
        {dam ? <FamilyNode member={dam} label="Dam" onClick={dam.id ? () => onSelect(dam.id!) : undefined} /> : <EmptyNode label="Dam" />}
        {sire ? <FamilyNode member={sire} label="Sire" onClick={sire.id ? () => onSelect(sire.id!) : undefined} /> : <EmptyNode label="Sire" />}
      </div>
      <div className="w-px h-6 bg-ink-300/30" />
      <FamilyNode
        member={{ id: animal.id, animal_id: animal.animal_id, animal_name: animal.animal_name, gender: animal.gender, primary_tag: animal.primary_tag, health_status: animal.health_status }}
        isCenter
      />
      {offspring.length > 0 && (
        <>
          <div className="w-px h-6 bg-ink-300/30" />
          <div className="flex flex-wrap justify-center gap-4">
            {offspring.map((o, i) => (
              <FamilyNode key={o.id ?? o.calf_record_id ?? i} member={o} label="Offspring" onClick={o.id ? () => onSelect(o.id!) : undefined} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function AnimalLifecyclePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [animalId, setAnimalId] = useState(searchParams.get('animal') ?? '');
  const { data: refData } = useReferenceData(['animals']);

  const selectAnimal = (id: string) => {
    setAnimalId(id);
    setSearchParams(id ? { animal: id } : {});
  };

  const lifecycleQuery = useQuery({
    queryKey: ['animal-lifecycle', animalId],
    queryFn: () => animalsApi.lifecycle(animalId),
    enabled: !!animalId,
  });
  const lc = lifecycleQuery.data;

  return (
    <div className="flex flex-col gap-4">
      <ILMSPageHeader
        title="Animal Lifecycle"
        subtitle="Family tree, lifetime production, growth and health — everything about one animal, in one place."
      />

      <ILMSCard>
        <div className="max-w-md">
          <label className="text-label text-ink-700 mb-1 block">Animal</label>
          <ILMSAnimalPicker value={animalId} onChange={selectAnimal} options={refData.animals} />
        </div>
      </ILMSCard>

      {!animalId && (
        <ILMSCard>
          <ILMSEmptyState icon={<PawPrint className="h-6 w-6" />} title="Pick an animal to see its lifecycle." description="Search by name/ID or scan its QR tag above." />
        </ILMSCard>
      )}

      {animalId && lifecycleQuery.isLoading && <ILMSCardSkeleton count={3} />}
      {animalId && lifecycleQuery.isError && <ILMSAlert tone="critical">{getApiErrorMessage(lifecycleQuery.error)}</ILMSAlert>}

      {lc && (
        <>
          <ILMSCard>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-page-title">{lc.animal.animal_id}{lc.animal.animal_name ? ` — ${lc.animal.animal_name}` : ''}</h2>
                  <ILMSStatusBadge status={lc.animal.health_status} size="sm" />
                  <ILMSStatusBadge status={lc.animal.production_status} size="sm" />
                </div>
                <p className="text-secondary text-ink-500 mt-0.5">
                  {lc.animal.animal_type_name ?? 'Animal'}{lc.animal.breed_name ? ` · ${lc.animal.breed_name}` : ''} · {lc.animal.gender} · {ageLabel(lc.age_days)} old
                  {lc.animal.primary_tag ? ` · Tag ${lc.animal.primary_tag}` : ''}
                </p>
              </div>
              <ILMSButton variant="outline" icon={<ArrowUpRight />} onClick={() => navigate(`/animals/${lc.animal.id}`)}>
                Open Full Profile
              </ILMSButton>
            </div>
          </ILMSCard>

          <ILMSCard title="Family Tree" subtitle="Click a relative to re-center the tree on them.">
            <FamilyTree lifecycle={lc} onSelect={selectAnimal} />
          </ILMSCard>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <ILMSKPI label="Lifetime Milk" value={`${formatNumber(lc.milk.total_litres, 1)} L`} icon={<Milk />} tone="success" />
            <ILMSKPI
              label="Avg Daily Yield"
              value={lc.milk.avg_daily_litres != null ? `${formatNumber(lc.milk.avg_daily_litres, 1)} L` : '—'}
              helperText={
                lc.milk.morning_fat_percent != null || lc.milk.evening_fat_percent != null
                  ? `Fat — Morning ${lc.milk.morning_fat_percent != null ? `${formatNumber(lc.milk.morning_fat_percent, 2)}%` : '—'} · Evening ${lc.milk.evening_fat_percent != null ? `${formatNumber(lc.milk.evening_fat_percent, 2)}%` : '—'}`
                  : undefined
              }
              icon={<TrendingUp />}
              tone="info"
            />
            <ILMSKPI label="Lactations" value={lc.milk.lactation_count} icon={<Repeat />} tone="neutral" />
            <ILMSKPI label="Calvings" value={lc.milk.calving_count} icon={<Baby />} tone="neutral" />
          </div>

          <ILMSCard title="Lifetime Milk Production" subtitle="Total litres by month, for as long as this animal has been recorded.">
            {lc.milk_trend.length === 0 ? (
              <p className="text-secondary text-ink-400 py-8 text-center">No milk sessions recorded for this animal yet.</p>
            ) : (
              <SmartChart id="animal-milk" data={lc.milk_trend} />
            )}
            {lc.milk_trend.some((m) => m.morning_fat_percent != null || m.evening_fat_percent != null) && (
              <div className="mt-4">
                <SmartChart id="animal-fat" data={lc.milk_trend} />
              </div>
            )}
          </ILMSCard>

          <ILMSCard title="Growth" subtitle="Weight over time.">
            {lc.growth.length === 0 ? (
              <p className="text-secondary text-ink-400 py-8 text-center">No growth/weight records for this animal yet.</p>
            ) : (
              <SmartChart id="animal-growth" data={lc.growth} />
            )}
          </ILMSCard>

          <ILMSCard title="Health Summary">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <ILMSKPI label="Health Records" value={lc.health.total_records} icon={<HeartPulse />} tone={lc.health.total_records > 0 ? 'warning' : 'success'} />
              <ILMSKPI label="Vaccinations" value={lc.health.vaccination_count} icon={<HeartPulse />} tone="info" />
              <ILMSKPI label="Treatments" value={lc.health.treatment_count} icon={<HeartPulse />} tone="neutral" />
              <ILMSKPI label="Last Record" value={lc.health.last_record_date ? formatDate(lc.health.last_record_date) : '—'} helperText={lc.health.last_diagnosis ?? undefined} icon={<HeartPulse />} tone="neutral" />
            </div>
            <ILMSButton variant="ghost" size="sm" onClick={() => navigate(`/animals/${lc.animal.id}`)}>
              View full health history on the animal's profile →
            </ILMSButton>
          </ILMSCard>

          <ILMSCard title="Lifetime Expenditure" subtitle="Acquisition cost, treatments, vet visits and deworming recorded against this animal.">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
              <ILMSKPI label="Total Spent" value={formatCurrency(lc.expenditure.total)} icon={<IndianRupee />} tone="neutral" />
              <ILMSKPI label="Line Items" value={lc.expenditure.items.length} icon={<Receipt />} tone="neutral" />
            </div>
            {lc.expenditure.trend.length === 0 ? (
              <p className="text-secondary text-ink-400 py-8 text-center">No costs recorded for this animal yet.</p>
            ) : (
              <>
                <SmartChart id="animal-expenditure" data={lc.expenditure.trend} />
                <div className="mt-4">
                  <ILMSTable
                    columns={EXPENDITURE_COLUMNS}
                    data={lc.expenditure.items}
                    rowKey={(r) => `${r.item_date}-${r.category}-${r.description}-${r.amount}`}
                    emptyTitle="No costs recorded for this animal yet."
                  />
                </div>
              </>
            )}
          </ILMSCard>
        </>
      )}
    </div>
  );
}

const EXPENDITURE_COLUMNS: ILMSTableColumn<LifecycleExpenditureItem>[] = [
  { key: 'date', header: 'Date', primary: true, render: (r) => formatDate(r.item_date) },
  { key: 'category', header: 'Category', render: (r) => r.category },
  { key: 'description', header: 'Description', render: (r) => r.description, hideOnTablet: true },
  { key: 'amount', header: 'Amount', align: 'right', render: (r) => formatCurrency(r.amount) },
];
